import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { convertUnderlineToSpace, formatSalaryRange } from "@ascendnow/shared";

import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import {
  CalendarIcon,
  DollarIcon,
  HeartFilledIcon,
  HeartIcon,
  LocationIcon,
} from "../../AscIcons";
import AscTag from "../../AscTag/AscTag";
import AscButton from "../../AscButton/AscButton";
import AscRichTextViewer from "../../AscRichTextEditor/AscRichTextViewer/AscRichTextViewer";
import AscLink from "../../AscLink/AscLink";
import AscImage from "../../AscImage/AscImage";
import styles from "././AscJobBoardJobCard.module.scss";
import { ReactComponent as BrowserSvg } from "../../../assets/icons/link.svg";
import PlaceHolderImage from "../../../assets/images/place-holder-image.svg";
import { useResponsive } from "libs/components/src/lib";

export interface AscJobBoardJobCardProps {
  jobTitle: string;
  logoUrl?: string;
  maxSalaryRange: number;
  minSalaryRange: number;
  jobLocationType?: string;
  jobType: string;
  country: string;
  isFavorite: boolean;
  city: string;
  id: string;
  link: string;
  description: string;
  isApplied?: boolean;
  datePosted: string;
  toggleSave: (id: string) => void;
  onApply?: (link: string) => void;
}

dayjs.extend(relativeTime);

export default function AscJobBoardJobCard({
  jobTitle,
  logoUrl,
  description,
  isFavorite,
  country,
  city,
  id,
  maxSalaryRange,
  minSalaryRange,
  datePosted,
  jobType,
  toggleSave,
  onApply,
  link,
  isApplied,
}: AscJobBoardJobCardProps) {
  const { isMedium } = useResponsive();

  const formatSalary = (salary: number | null) => {
    if (salary === null || salary === 0) {
      return "Non paid";
    } else if (salary < 1000) {
      return salary.toString();
    } else {
      return (salary / 1000).toFixed(0);
    }
  };

  let minSalary = formatSalary(minSalaryRange);
  let maxSalary = formatSalary(maxSalaryRange);

  if (
    (minSalaryRange === null || minSalaryRange === 0) &&
    (maxSalaryRange === null || maxSalaryRange === 0)
  ) {
    minSalary = "Non paid";
    maxSalary = "";
  } else {
    if (minSalary !== "Non paid") {
      minSalary = `${minSalary}`;
    }
    if (maxSalary !== "Non paid") {
      maxSalary = `${maxSalary}K`;
    }
  }

  const FavoriteButton = () => (
    <div
      className={styles.favorite}
      onClick={() => toggleSave(id)}
      data-testid="asc-job-card-saved-icon-wrapper"
    >
      {isFavorite ? (
        <HeartFilledIcon
          className={styles.favoriteIcon}
          data-testid="asc-job-card-saved-icon"
        />
      ) : (
        <HeartIcon
          className={styles.favoriteIcon}
          data-testid="asc-job-card-not-saved-icon"
        />
      )}
    </div>
  );

  return (
    <AscGenericCard
      className={classNames(styles.card)}
      bodyClassName={styles.cardBody}
      contentClassName={styles.cardContent}
      title={
        <div className={styles.cardHeader}>
          <AscParagraph className={styles.cardHeaderSubtitle}>
            Job description
          </AscParagraph>
          <div className={styles.cardHeaderContent}>
            <div className={styles.cardTitleWrapper}>
              <AscParagraph className={styles.cardTitle}>
                {jobTitle}
              </AscParagraph>
              {isApplied && <AscTag label="Applied" />}
            </div>
            {isMedium && <FavoriteButton />}
          </div>
        </div>
      }
      customImage={
        <div className={styles.cardImageWrapper}>
          <div className={styles.cardImage}>
            <AscImage src={logoUrl || PlaceHolderImage} alt={jobTitle} />
            <AscLink
              className={styles.cardImageLink}
              innerWrapperTag={null}
              href={link}
              target="_blank"
            >
              <BrowserSvg />
            </AscLink>
          </div>
          <div className={styles.favoriteOrDate}>
            {isMedium ? (
              <span className={styles.datePosted}>
                Posted {dayjs(datePosted).fromNow()}
              </span>
            ) : (
              <FavoriteButton />
            )}
          </div>
        </div>
      }
      footer={
        <div className={styles.cardFooter}>
          <AscParagraph className={styles.cardFooterTitle}>
            Description
          </AscParagraph>
          <div className={styles.cardFooterContent}>
            {description && (
              <AscRichTextViewer
                className={styles.cardFooterDescription}
                inferValue={description}
              />
            )}
          </div>
        </div>
      }
    >
      <div className={styles.cardContentDetailList}>
        <span className={styles.cardContentDetail}>
          <LocationIcon className={styles.icon} />
          <span className={styles.label}>
            {country} / {city}
          </span>
        </span>
        <span className={styles.cardContentDetail}>
          <CalendarIcon className={styles.icon} />
          {jobType && (
            <span className={styles.label}>
              {convertUnderlineToSpace(jobType)}
            </span>
          )}
        </span>
        <span className={styles.cardContentDetail}>
          <DollarIcon className={styles.icon} />
          <span className={styles.label}>
            {formatSalaryRange(minSalaryRange, maxSalaryRange)}
          </span>
        </span>
      </div>
      {!isApplied && (
        <AscButton onClick={() => onApply && onApply(link)}>Apply</AscButton>
      )}
    </AscGenericCard>
  );
}
